import { CATEGORY_LABELS, INSTRUMENT_LABELS, JURISDICTION_LABELS } from '../../data/regulations/taxonomy'
import type { RegisterQuery, RegulatoryRecord } from '../../data/regulations/types'
import { compareIso } from './dates'

export function normaliseSearch(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ')
}

export function recordSearchText(record: RegulatoryRecord): string {
  return normaliseSearch(
    [
      record.title,
      record.shortTitle,
      record.authority,
      record.summary,
      record.applicabilitySummary,
      record.instrumentType,
      ...record.categories.map((id) => CATEGORY_LABELS[id]),
      ...record.jurisdiction.map((id) => JURISDICTION_LABELS[id]),
      INSTRUMENT_LABELS[record.instrumentType],
    ].join(' '),
  )
}

export function matchesQuery(record: RegulatoryRecord, query: RegisterQuery): boolean {
  const search = normaliseSearch(query.search || '')
  if (search && !recordSearchText(record).includes(search)) return false
  if (query.jurisdictions?.length && !query.jurisdictions.some((id) => record.jurisdiction.includes(id))) return false
  if (query.categories?.length && !query.categories.some((id) => record.categories.includes(id))) return false
  if (query.instrumentTypes?.length && !query.instrumentTypes.includes(record.instrumentType)) return false
  if (query.statuses?.length && !query.statuses.includes(record.status)) return false
  if (query.listing === 'listed' && !['listed', 'both', 'not_specific'].includes(record.listingScope)) return false
  if (query.listing === 'unlisted' && !['unlisted', 'both', 'not_specific'].includes(record.listingScope)) return false
  if (query.financial === 'financial' && !['financial', 'both', 'not_specific'].includes(record.financialScope)) return false
  if (query.financial === 'non_financial' && !['non_financial', 'both', 'not_specific'].includes(record.financialScope)) return false
  if (query.establishment === 'mainland' && !['mainland', 'both', 'not_specific'].includes(record.establishmentScope)) return false
  if (query.establishment === 'free_zone' && !['free_zone', 'both', 'not_specific'].includes(record.establishmentScope)) return false
  if (query.sector && !(record.sectors.includes(query.sector) || record.sectors.includes('cross_sector'))) return false
  if (query.obligation && record.obligationLevel !== query.obligation) return false
  if (query.confirmedDeadlinesOnly) {
    const confirmed = record.deadlines.some((deadline) => deadline.certainty === 'confirmed' && Boolean(deadline.date))
    if (!confirmed) return false
  }
  return true
}

export function filterRecords(records: RegulatoryRecord[], query: RegisterQuery): RegulatoryRecord[] {
  const direction = query.sortDirection === 'desc' ? -1 : 1
  const filtered = records.filter((record) => matchesQuery(record, query))
  const sort = query.sort || 'title'
  filtered.sort((a, b) => {
    let result = 0
    if (sort === 'title') result = a.title.localeCompare(b.title)
    else if (sort === 'authority') result = a.authority.localeCompare(b.authority)
    else if (sort === 'status') result = a.status.localeCompare(b.status)
    else if (sort === 'publication') result = compareIso(a.publicationDate, b.publicationDate)
    else if (sort === 'effective') result = compareIso(a.effectiveDate, b.effectiveDate)
    if (result === 0) result = a.title.localeCompare(b.title)
    return result * direction
  })
  return filtered
}

export function paginate<T>(items: T[], page: number, pageSize: number): { items: T[]; page: number; pages: number; total: number } {
  const total = items.length
  const pages = Math.max(1, Math.ceil(total / pageSize))
  const safePage = Math.min(Math.max(page, 1), pages)
  const start = (safePage - 1) * pageSize
  return { items: items.slice(start, start + pageSize), page: safePage, pages, total }
}
